import React, { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { Filter, Star, Bookmark, LogOut, Radio, Sparkles, GitCompareArrows, Wallet, Sun, Moon, Search, CalendarDays, FlaskConical, Grid3x3, Menu, X, BellRing } from "lucide-react";
import Logo from "@/components/Logo";
import Footer from "@/components/Footer";
import GlobalSearch from "@/components/GlobalSearch";
import AlertsBell from "@/components/AlertsBell";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";

const tabs = [
  { to: "/", label: "Teknik Tarama", icon: Filter, testId: "nav-screener" },
  { to: "/heatmap", label: "Isı Haritası", icon: Grid3x3, testId: "nav-heatmap" },
  { to: "/daily-summary", label: "Günün Özeti", icon: CalendarDays, testId: "nav-daily" },
  { to: "/signals", label: "Sinyal Akışı", icon: Radio, testId: "nav-signals" },
  { to: "/presets", label: "Hazır Stratejiler", icon: Sparkles, testId: "nav-presets" },
  { to: "/backtest", label: "Backtest", icon: FlaskConical, testId: "nav-backtest" },
  { to: "/compare", label: "Karşılaştırma", icon: GitCompareArrows, testId: "nav-compare" },
  { to: "/portfolio", label: "Portföy", icon: Wallet, testId: "nav-portfolio" },
  { to: "/alerts-rules", label: "Alarm Kuralları", icon: BellRing, testId: "nav-alerts-rules" },
  { to: "/watchlist", label: "İzleme Listem", icon: Star, testId: "nav-watchlist" },
  { to: "/saved-scans", label: "Kayıtlı Taramalar", icon: Bookmark, testId: "nav-saved" },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((s) => !s);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="min-h-screen bg-bg-base flex flex-col">
      <header className="sticky top-0 z-50 h-16 border-b border-bg-border bg-bg-base/95 backdrop-blur-md px-3 sm:px-6 flex items-center justify-between">
        <div className="flex items-center gap-4 sm:gap-6 min-w-0">
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setMenuOpen(true)}
            className="lg:hidden w-9 h-9 rounded-lg border border-bg-border hover:bg-bg-hover flex items-center justify-center text-text-secondary hover:text-text-primary"
            aria-label="Menü"
          ><Menu size={18} /></button>
          <Logo />
          <div className="hidden xl:flex items-center gap-1.5 text-xs text-text-secondary" title="yfinance kaynağı, ~15-20 dk gecikmeli BIST verisi">
            <Radio size={12} className="text-signal-up animate-pulse" />
            <span>Piyasa Verisi · Gecikmeli</span>
          </div>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2">
          <button
            data-testid="global-search-open"
            onClick={() => setSearchOpen(true)}
            className="hidden md:flex items-center gap-2 bg-bg-panel border border-bg-border text-text-secondary hover:text-text-primary hover:border-brand-blue/50 px-3 h-9 rounded-lg text-xs transition-colors"
          >
            <Search size={14} />
            <span>Hisse ara...</span>
            <kbd className="text-[10px] bg-bg-base border border-bg-border rounded px-1.5 py-0.5 ml-3">⌘K</kbd>
          </button>
          <button
            data-testid="global-search-open-mobile"
            onClick={() => setSearchOpen(true)}
            className="md:hidden w-9 h-9 rounded-lg border border-bg-border hover:bg-bg-hover flex items-center justify-center text-text-secondary"
          ><Search size={15} /></button>

          <AlertsBell />

          <button
            data-testid="theme-toggle"
            onClick={toggle}
            className="w-9 h-9 rounded-lg border border-bg-border hover:bg-bg-hover flex items-center justify-center text-text-secondary hover:text-text-primary transition-colors"
            title={theme === "dark" ? "Aydınlık tema" : "Karanlık tema"}
          >{theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}</button>

          <div className="hidden xl:block text-xs text-text-secondary px-2">
            <span className="text-text-primary font-medium">{user?.name || user?.email}</span>
          </div>
          <button
            data-testid="logout-btn"
            onClick={async () => { await logout(); nav("/login"); }}
            className="flex items-center gap-1.5 text-xs text-text-secondary hover:text-signal-down transition-colors px-1 sm:px-2"
            title="Çıkış"
          ><LogOut size={14} /><span className="hidden xl:inline">Çıkış</span></button>
        </div>
      </header>

      {/* Desktop nav bar */}
      <nav className="hidden lg:flex items-center gap-1 border-b border-bg-border bg-bg-panel px-4 sm:px-6 overflow-x-auto">
        {tabs.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.to === "/"}
            data-testid={t.testId}
            className={({ isActive }) =>
              `flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                isActive
                  ? "text-brand-blue border-brand-blue"
                  : "text-text-secondary border-transparent hover:text-text-primary"
              }`
            }
          ><t.icon size={15} />{t.label}</NavLink>
        ))}
      </nav>

      {/* Mobile drawer */}
      {menuOpen && (
        <div className="lg:hidden fixed inset-0 z-[90]" onClick={() => setMenuOpen(false)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            data-testid="mobile-menu-panel"
            onClick={(e) => e.stopPropagation()}
            className="absolute left-0 top-0 h-full w-72 max-w-[85vw] bg-bg-panel border-r border-bg-border overflow-y-auto"
          >
            <div className="p-4 border-b border-bg-border flex items-center justify-between">
              <Logo size={36} />
              <button onClick={() => setMenuOpen(false)} className="text-text-secondary hover:text-text-primary"><X size={18} /></button>
            </div>
            <nav className="p-2">
              {tabs.map((t) => (
                <NavLink
                  key={t.to}
                  to={t.to}
                  end={t.to === "/"}
                  data-testid={`m-${t.testId}`}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-3 text-sm font-medium rounded-lg mb-1 transition-colors ${
                      isActive ? "bg-brand-blue text-white" : "text-text-secondary hover:bg-bg-hover hover:text-text-primary"
                    }`
                  }
                ><t.icon size={16} />{t.label}</NavLink>
              ))}
            </nav>
            <div className="p-3 border-t border-bg-border mt-2">
              <div className="text-[11px] text-text-secondary mb-2">Oturum</div>
              <div className="text-sm text-text-primary font-medium truncate">{user?.name || user?.email}</div>
            </div>
          </div>
        </div>
      )}

      <main className="px-3 sm:px-6 py-4 sm:py-6 max-w-[1600px] mx-auto w-full flex-1">
        <Outlet />
      </main>

      <Footer />

      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
}
